import {
  DXF_ENTITY_TYPE,
  DXF_MAPPING_ROLE,
  type DxfMappingRole,
} from '@frontend/api';

/**
 * A role's canvas presence: the stroke the WebGL overlay actually paints.
 *
 * Its own object, separate from `MappingRoleDefinition.color`, because the two
 * are not always the same color. Ignore is the case that forces the split: its
 * UI chip is `interaction.disabled`, a light grey meant for a *fill* a label sits
 * on, which is close to invisible as a *line* over the canvas — so the canvas
 * stroke uses its own fixed mid-grey instead. Every other role happens to share
 * hex values between the two today, but keeping them apart means a role that
 * ever needs to differ does not have to fight this shape.
 */
export type RoleStrokeStyle = {
  /** CSS hex. Not a theme token — this paints inside WebGL, not through Chakra. */
  readonly colorHex: string;
  /** Stroke width in CSS pixels. */
  readonly widthPx: number;
  /**
   * `[onLength, offLength]`, calibrated at 1 screen pixel per world unit, or
   * `null` for a solid stroke (Cut).
   *
   * The renderer divides each number by the live zoom scale every frame — see
   * `syncMaterialDashes` in `@frontend/viewer-three` — so the dash keeps a
   * constant apparent size on screen: without that, it would shrink into a
   * solid-looking blur zoomed out and stretch into a few long dashes zoomed in.
   */
  readonly dash: readonly [number, number] | null;
};

/**
 * The presentation side of a mapping role: what to call it, what color to paint
 * it, and what geometry it may apply to.
 *
 * One registry, read by the mapping panel, the role flyout, the mapping toolbar and
 * the overlay builder, so adding a role is one entry here rather than four edits
 * across three layers. That is the concrete form of the "future-proof for
 * machining" requirement: drilling, tapping, boring and the rest arrive as rows in
 * this table plus a value on the wire enum, and every surface picks them up.
 *
 * The *rules* deliberately do not live here — `areRolesCoApplicable` and
 * `canAssignRoleToEntity` are in `@frontend/api`, shared with the write validator,
 * so the frontend and the backend cannot disagree about what is legal. This file
 * only knows how to show what those rules allow.
 */
export type MappingRoleDefinition = {
  readonly role: DxfMappingRole;
  /**
   * Which manufacturing story the role belongs to. Machining roles are not
   * implemented, but the grouping is what the panel's section headings will read
   * from when they are.
   */
  readonly family: 'laser' | 'machining' | 'general';
  /** Short label, as it appears on a button and in the panel. */
  readonly label: string;
  /**
   * UI chrome color — the toolbar's active brush fill, the floating picker's
   * active state, context-menu swatches, the mapping panel's dots. A theme
   * token (e.g. `alert.error`), not a literal hex, so these stay in step with
   * the rest of the app's alert palette. See `stroke` for the canvas color.
   */
  readonly color: string;
  /** The canvas overlay stroke — color, weight, and dash. */
  readonly stroke: RoleStrokeStyle;
  /**
   * Paint order, low to high. Cut is last so a part outline stays legible where it
   * runs alongside an etch or a bend line.
   */
  readonly drawOrder: number;
  /**
   * Source entity types the role may apply to. Empty means any.
   *
   * Only bending constrains itself today — the product rule is that a bend line is
   * a straight line — but the field is on every row so a machining role can bring
   * its own constraint without changing the shape callers read.
   */
  readonly allowedEntityTypes: ReadonlySet<string>;
};

const ANY_ENTITY_TYPE: ReadonlySet<string> = new Set();

/**
 * Canvas stroke hexes, one per role.
 *
 * Literal hex, not theme tokens: these paint inside WebGL via `new
 * Color(colorHex)`, which has no notion of a Chakra token. Named here rather
 * than inlined below so each one has a place to say what it is and why — in
 * particular why Ignore's does not just reuse its own UI token, and why the
 * others do not reuse `alert.*`'s hex even though today it is the same value.
 */
const CUT_STROKE_HEX = '#E11D1D';
const ETCH_STROKE_HEX = '#1DC502';
const BEND_STROKE_HEX = '#F59300';
/**
 * Fixed mid-grey, deliberately not `interaction.disabled` (Ignore's own UI
 * chip color, used below): that token is a light fill meant to sit *behind*
 * a label, and is close to invisible as a *line* over the canvas.
 */
export const IGNORE_STROKE_HEX = '#6E6E6E';

export const CUT_ROLE_DEFINITION: MappingRoleDefinition = {
  role: DXF_MAPPING_ROLE.CUTTING,
  family: 'laser',
  label: 'Cut',
  color: 'alert.error',
  stroke: { colorHex: CUT_STROKE_HEX, widthPx: 2, dash: null },
  drawOrder: 3,
  allowedEntityTypes: ANY_ENTITY_TYPE,
};
export const ETCH_ROLE_DEFINITION: MappingRoleDefinition = {
  role: DXF_MAPPING_ROLE.ETCHING,
  family: 'laser',
  label: 'Etch',
  color: 'alert.success',
  stroke: { colorHex: ETCH_STROKE_HEX, widthPx: 1.5, dash: [4, 4] },
  drawOrder: 1,
  allowedEntityTypes: ANY_ENTITY_TYPE,
};
export const BEND_ROLE_DEFINITION: MappingRoleDefinition = {
  role: DXF_MAPPING_ROLE.BENDING,
  family: 'laser',
  label: 'Bend',
  color: 'alert.warning',
  stroke: { colorHex: BEND_STROKE_HEX, widthPx: 1.5, dash: [8, 4] },
  drawOrder: 2,
  allowedEntityTypes: new Set([DXF_ENTITY_TYPE.LINE]),
};
export const IGNORE_ROLE_DEFINITION: MappingRoleDefinition = {
  role: DXF_MAPPING_ROLE.IGNORE,
  family: 'general',
  label: 'Ignore',
  color: 'interaction.disabled',
  stroke: { colorHex: IGNORE_STROKE_HEX, widthPx: 1, dash: [2, 2] },
  drawOrder: 0,
  allowedEntityTypes: ANY_ENTITY_TYPE,
};

export const MAPPING_ROLE_DEFINITIONS: readonly MappingRoleDefinition[] = [
  CUT_ROLE_DEFINITION,
  ETCH_ROLE_DEFINITION,
  BEND_ROLE_DEFINITION,
  IGNORE_ROLE_DEFINITION,
];

const BY_ROLE: ReadonlyMap<DxfMappingRole, MappingRoleDefinition> = new Map(
  MAPPING_ROLE_DEFINITIONS.map((definition) => [definition.role, definition]),
);

export const mappingRoleDefinition = (
  role: DxfMappingRole,
): MappingRoleDefinition | undefined => BY_ROLE.get(role);

/** Definitions in paint order, low to high. */
export const MAPPING_ROLES_IN_DRAW_ORDER: readonly MappingRoleDefinition[] = [
  ...MAPPING_ROLE_DEFINITIONS,
].sort((a, b) => a.drawOrder - b.drawOrder);

/**
 * The button/chip foreground for a role's *active* UI state.
 *
 * Every role but one uses `page.surface` (white-on-color) — Ignore's chip fill,
 * `interaction.disabled`, is too light for that to read, so it gets a strong
 * foreground token instead. One exception isn't worth a field on the registry;
 * this is the one place that special case lives, so the toolbar and the flyout
 * cannot land on two different answers for it.
 */
export const activeRoleLabelColor = (role: DxfMappingRole): string =>
  role === DXF_MAPPING_ROLE.IGNORE ? 'feature.primary' : 'page.surface';
